import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, Paper, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { collection, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import { db } from "../Config/firebase";
import { aplicarTenant, getUsuarioAtualComTenant } from "../Utils/tenant";
import RelatorioVerificacaoCompetenciasPrint from "./RelatorioVerificacaoCompetenciasPrint";

const COLLECTION = "tblVerificacaoCompetencias";
const TECNICAS = [
  ["Conhecimento Normas Técnicas", "ABNT NBR 5738, 5739, 7212, ISO 9001, ISO 17025"],
  ["Procedimentos de Moldagem", "Coleta e moldagem de corpos de prova"],
  ["Ensaio de Rompimento", "Operação de prensa e registro de resultados"],
  ["Caracterização de Agregados", "Ensaios em graúdo e miúdo"],
  ["Interpretação de Resultados", "Preenchimento de laudos e relatórios"],
  ["Uso de Equipamentos", "Balanças, peneiras, prensas, EPIs"],
];
const COMPORTAMENTAIS = [
  ["Trabalho em Equipe", "Colaboração e integração com colegas"],
  ["Comunicação", "Clareza ao relatar informações"],
  ["Proatividade", "Antecipação de problemas, sugestões"],
  ["Organização", "Cumprimento de prazos e rotinas"],
  ["Atenção a Detalhes", "Rigor técnico e cuidado nos ensaios"],
  ["Comprometimento Ético", "Cumprimento de normas e valores"],
];
const CONCLUSOES = ["Apto", "Em desenvolvimento", "Não apto"];

function identificarUsuario(usuario) {
  if (!usuario) return "";
  const nome = String(usuario.nome || "").trim();
  const email = String(usuario.email || "").trim();
  return nome && email ? `${nome} - ${email}` : nome || email;
}

function criarFicha(usuario = null) {
  const agora = new Date();
  const data = new Date(agora.getTime() - agora.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const itens = (lista) => lista.map(([item, descricao]) => ({ item, descricao, nota: "", observacoes: "" }));
  return {
    codigoDocumento: "RQ 5.3", versao: "01", aprovadoEm: "2025-09-20",
    avaliado: "", avaliador: identificarUsuario(usuario), avaliadorUsuario: usuario ? { uid: usuario.uid || "", nome: usuario.nome || "", email: usuario.email || "" } : null, data, cargoFuncao: "",
    tecnicas: itens(TECNICAS), comportamentais: itens(COMPORTAMENTAIS),
    conclusao: "", planoAcao: "", assinaturaAvaliador: identificarUsuario(usuario), assinaturaDirecao: "",
    aprovadoDirecao: false, aprovacaoNome: "", aprovacaoData: "", aprovacaoUsuario: null, aprovadoEmDirecao: null,
  };
}

function Secao({ titulo, children }) {
  return <Paper sx={{ border: "1px solid #d7e0ea", mb: 2, overflow: "hidden" }}><Typography fontWeight={700} sx={{ backgroundColor: "#d6e6f6", px: 2, py: 1 }}>{titulo}</Typography><Box sx={{ p: 2 }}>{children}</Box></Paper>;
}

function TabelaCompetencias({ itens, tituloNota, onChange, disabled }) {
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 750 }}>
        <TableHead sx={{ backgroundColor: "#f8fafc" }}><TableRow><TableCell>Item avaliado</TableCell><TableCell>Descrição</TableCell><TableCell>{tituloNota} (1 a 5)</TableCell><TableCell>Observações</TableCell></TableRow></TableHead>
        <TableBody>{itens.map((item, indice) => (
          <TableRow key={indice}>
            <TableCell sx={{ width: "22%", fontWeight: 700 }}>{item.item}</TableCell>
            <TableCell sx={{ width: "30%" }}>{item.descricao}</TableCell>
            <TableCell sx={{ width: 140 }}><TextField select label={tituloNota} size="small" fullWidth disabled={disabled} value={item.nota} onChange={(event) => onChange(indice, "nota", event.target.value)} inputProps={{ "aria-label": `${tituloNota}: ${item.item}` }}><MenuItem value="">Não informado</MenuItem>{[1, 2, 3, 4, 5].map((nota) => <MenuItem key={nota} value={nota}>{nota}</MenuItem>)}</TextField></TableCell>
            <TableCell><TextField label="Observações" size="small" multiline minRows={2} fullWidth disabled={disabled} value={item.observacoes} onChange={(event) => onChange(indice, "observacoes", event.target.value)} inputProps={{ "aria-label": `Observações: ${item.item}` }} /></TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
    </Box>
  );
}

export default function DetalhesVerificacaoCompetencias() {
  const { registroId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [ficha, setFicha] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const novaReferencia = useRef(null);
  const salvamentoEmCurso = useRef(false);
  const ultimaFichaSalva = useRef("");

  useEffect(() => {
    let ativo = true;
    setFicha(null);
    setCarregando(true);
    setAviso(ultimaFichaSalva.current === registroId ? { tipo: "success", texto: "Ficha salva com sucesso." } : null);
    novaReferencia.current = null;
    async function carregar() {
      try {
        if (registroId === "novo") {
          const funcionarioId = new URLSearchParams(location.search).get("funcionarioId");
          if (!funcionarioId) {
            history.replace("/app/verificacao-competencias");
            return;
          }
          const usuario = await getUsuarioAtualComTenant();
          setUsuarioLogado(usuario);
          const snap = await getDoc(doc(db, "dp_funcionarios", funcionarioId));
          if (!snap.exists()) throw new Error("Colaborador não encontrado.");
          const funcionario = snap.data();
          if (!usuario.isSuperadmin && (!usuario.tenantId || funcionario.tenantId !== usuario.tenantId)) throw new Error("Colaborador não disponível para sua empresa.");
          if (!ativo) return;
          novaReferencia.current = doc(collection(db, COLLECTION));
          setFicha({ ...criarFicha(usuario), funcionarioId, avaliado: funcionario.nome || "", cargoFuncao: funcionario.cargo || "" });
          return;
        }
        const usuario = await getUsuarioAtualComTenant();
        setUsuarioLogado(usuario);
        const snap = await getDoc(doc(db, COLLECTION, registroId));
        if (!snap.exists()) throw new Error("Ficha não encontrada.");
        const dados = snap.data();
        if (!usuario.isSuperadmin && (!usuario.tenantId || dados.tenantId !== usuario.tenantId)) throw new Error("Ficha não disponível para sua empresa.");
        if (ativo) setFicha({ ...criarFicha(), ...dados, id: snap.id });
      } catch (error) {
        console.error("Erro ao carregar ficha de competências:", error);
        if (ativo) setAviso({ tipo: "error", texto: "Não foi possível abrir a ficha. Verifique se ela existe e se você possui permissão de acesso." });
      } finally {
        if (ativo) setCarregando(false);
      }
    }
    carregar();
    return () => { ativo = false; };
  }, [registroId, location.search, history]);

  function atualizar(campo, valor) {
    setAviso(null);
    setFicha((atual) => ({ ...atual, [campo]: valor }));
  }

  function atualizarItem(grupo, indice, campo, valor) {
    setAviso(null);
    setFicha((atual) => ({ ...atual, [grupo]: atual[grupo].map((item, i) => i === indice ? { ...item, [campo]: valor } : item) }));
  }

  function alternarAprovacao(aprovado) {
    if (aprovado && !ficha.aprovacaoData) {
      setAviso({ tipo: "error", texto: "Informe a data da aprovação antes de confirmar." });
      return;
    }
    const aprovador = identificarUsuario(usuarioLogado);
    setAviso(null);
    setFicha((atual) => ({
      ...atual,
      aprovadoDirecao: aprovado,
      aprovacaoNome: aprovado ? aprovador : "",
      assinaturaDirecao: aprovado ? aprovador : "",
      aprovacaoUsuario: aprovado ? { uid: usuarioLogado?.uid || "", nome: usuarioLogado?.nome || "", email: usuarioLogado?.email || "" } : null,
      aprovadoEmDirecao: aprovado ? new Date() : null,
    }));
  }

  async function salvar(exigirAprovacaoCompleta = false) {
    if (!ficha || salvamentoEmCurso.current) return;
    if (!ficha.avaliado.trim() || !ficha.avaliador.trim() || !ficha.data || !ficha.cargoFuncao.trim()) {
      setAviso({ tipo: "error", texto: "Informe avaliado, avaliador, data e cargo/função." });
      return;
    }
    const itens = [...ficha.tecnicas, ...ficha.comportamentais];
    if (itens.some((item) => item.nota !== "" && ![1, 2, 3, 4, 5].includes(Number(item.nota)))) {
      setAviso({ tipo: "error", texto: "As notas devem ser números inteiros de 1 a 5." });
      return;
    }
    if (exigirAprovacaoCompleta && itens.some((item) => !String(item.nota ?? "").trim())) {
      setAviso({ tipo: "error", texto: "Preencha o resultado ou a nota de todas as competências técnicas e comportamentais." });
      return;
    }
    if (exigirAprovacaoCompleta && !String(ficha.conclusao || "").trim()) {
      setAviso({ tipo: "error", texto: "Informe a conclusão da avaliação." });
      return;
    }
    if (exigirAprovacaoCompleta && !ficha.aprovacaoData) {
      setAviso({ tipo: "error", texto: "Informe a data da aprovação." });
      return;
    }
    if (exigirAprovacaoCompleta && ficha.aprovadoDirecao !== true) {
      setAviso({ tipo: "error", texto: "Marque a confirmação de que leu e aprovou a avaliação." });
      return;
    }
    if (ficha.aprovadoDirecao === true && !ficha.aprovacaoData) {
      setAviso({ tipo: "error", texto: "Informe a data da aprovação." });
      return;
    }
    salvamentoEmCurso.current = true;
    setSalvando(true);
    setAviso(null);
    try {
      const usuario = await getUsuarioAtualComTenant();
      const tenantId = usuario.tenantId || (usuario.isSuperadmin ? localStorage.getItem("tenantId") : "");
      if (!ficha.id && !tenantId) throw new Error("Selecione uma empresa antes de criar a ficha.");
      if (!usuario.isSuperadmin && ficha.id && ficha.tenantId !== usuario.tenantId) throw new Error("Empresa divergente.");
      const fichaParaSalvar = { ...ficha, assinaturaAvaliador: ficha.avaliador || "", assinaturaDirecao: ficha.aprovadoDirecao === true ? ficha.aprovacaoNome || "" : "" };
      const { id, ...dados } = fichaParaSalvar;
      const atualizadoEm = new Date();
      if (id) {
        await updateDoc(doc(db, COLLECTION, id), { ...dados, atualizadoEm });
        setFicha((atual) => ({ ...atual, assinaturaAvaliador: dados.assinaturaAvaliador, assinaturaDirecao: dados.assinaturaDirecao, atualizadoEm }));
      } else {
        const ref = novaReferencia.current;
        const novosDados = aplicarTenant({ ...dados, criadoEm: atualizadoEm, atualizadoEm }, { ...usuario, tenantId });
        await setDoc(ref, novosDados);
        setFicha({ ...novosDados, id: ref.id });
        ultimaFichaSalva.current = ref.id;
        history.replace(`/app/verificacao-competencias/${ref.id}`);
      }
      setAviso({ tipo: "success", texto: exigirAprovacaoCompleta ? "Aprovação salva com sucesso." : "Ficha salva com sucesso." });
    } catch (error) {
      console.error("Erro ao salvar ficha de competências:", error);
      setAviso({ tipo: "error", texto: "Não foi possível salvar a ficha. Verifique sua conexão e as permissões de acesso e tente novamente." });
    } finally {
      salvamentoEmCurso.current = false;
      setSalvando(false);
    }
  }

  const campo = (nome, label, props = {}) => <TextField label={label} value={ficha[nome]} onChange={(event) => atualizar(nome, event.target.value)} disabled={salvando} fullWidth {...props} />;

  const modoRelatorio = new URLSearchParams(location.search).get("print") === "1";
  if (ficha && !carregando && modoRelatorio) {
    return <RelatorioVerificacaoCompetenciasPrint ficha={ficha} onVoltar={() => history.push(`/app/verificacao-competencias/${ficha.id}`)} />;
  }

  return (
    <>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Paper sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2, p: 2 }}>
          <Box><Typography variant="h5" fontWeight={700}>Verificação de Competências e Habilidades</Typography><Typography color="text.secondary">RQ 5.3 • Versão 01 • Aprovado em 20/09/2025</Typography></Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} disabled={salvando} onClick={() => history.push("/app/verificacao-competencias")}>Voltar</Button>
            <Button variant="outlined" startIcon={<VisibilityIcon />} disabled={!ficha?.id || salvando} onClick={() => history.push(`/app/verificacao-competencias/${ficha.id}?print=1`)}>Visualizar relatório</Button>
            <Button variant="contained" startIcon={<SaveIcon />} disabled={!ficha || salvando || carregando} onClick={() => salvar()}>{salvando ? "Salvando..." : "Salvar"}</Button>
          </Stack>
        </Paper>
        {carregando && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>}
        {ficha && !carregando && <>
          <Secao titulo="Identificação">
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>{campo("avaliado", "Avaliado", { required: true, InputProps: { readOnly: Boolean(ficha.funcionarioId) } })}{campo("avaliador", "Avaliador", { required: true, helperText: "Usuário autenticado responsável pela avaliação.", InputProps: { readOnly: true } })}</Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>{campo("data", "Data", { type: "date", required: true, InputLabelProps: { shrink: true } })}{campo("cargoFuncao", "Cargo/Função", { required: true })}</Stack>
            </Stack>
          </Secao>
          <Secao titulo="1. Competências Técnicas"><TabelaCompetencias itens={ficha.tecnicas} tituloNota="Resultado" disabled={salvando} onChange={(indice, nome, valor) => atualizarItem("tecnicas", indice, nome, valor)} /></Secao>
          <Secao titulo="2. Competências Comportamentais"><TabelaCompetencias itens={ficha.comportamentais} tituloNota="Nota" disabled={salvando} onChange={(indice, nome, valor) => atualizarItem("comportamentais", indice, nome, valor)} /></Secao>
          <Secao titulo="3. Conclusão da Avaliação">
            <Stack spacing={2}>
              <TextField select label="Conclusão" value={ficha.conclusao} disabled={salvando} onChange={(event) => atualizar("conclusao", event.target.value)} fullWidth><MenuItem value="">Não informada</MenuItem>{CONCLUSOES.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</TextField>
              {campo("planoAcao", "Recomendações / Plano de Ação", { multiline: true, minRows: 4 })}
            </Stack>
          </Secao>
          <Secao titulo="4. Aprovação da Direção">
            <Stack spacing={2}>
              <Typography color="text.secondary">A direção deve ler a avaliação completa e registrar sua aprovação com o próprio usuário autenticado.</Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField label="Usuário responsável pela aprovação" value={ficha.aprovacaoNome || identificarUsuario(usuarioLogado)} helperText="Nome e e-mail obtidos automaticamente do usuário autenticado." InputProps={{ readOnly: true }} fullWidth />
                <TextField label="Data da aprovação" type="date" InputLabelProps={{ shrink: true }} value={ficha.aprovacaoData || ""} onChange={(event) => atualizar("aprovacaoData", event.target.value)} disabled={salvando} fullWidth />
              </Stack>
              <FormControlLabel control={<Checkbox checked={ficha.aprovadoDirecao === true} onChange={(event) => alternarAprovacao(event.target.checked)} disabled={salvando} />} label="Li a avaliação completa e aprovo esta verificação de competências e habilidades." />
              {ficha.aprovadoDirecao === true && <Typography color="success.main" fontWeight={700}>Aprovado por {ficha.aprovacaoNome}.</Typography>}
              <Button variant="contained" startIcon={<SaveIcon />} onClick={() => salvar(true)} disabled={salvando} sx={{ alignSelf: "flex-end" }}>{salvando ? "Salvando..." : "Salvar aprovação"}</Button>
            </Stack>
          </Secao>
        </>}
      </Box>
      <Snackbar
        open={Boolean(aviso)}
        autoHideDuration={7000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setAviso(null)}
      >
        <Alert severity={aviso?.tipo || "info"} variant="filled" onClose={() => setAviso(null)} sx={{ width: "100%" }}>
          {aviso?.texto || ""}
        </Alert>
      </Snackbar>
    </>
  );
}
